import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/react-hooks";
import * as Sentry from "@sentry/browser";
import { message } from "antd";
import gql from "fraql";
import moment from "moment";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import get from "lodash/get";
import { FIELD_TYPES } from "../../constants/fieldConstants";
import { USER_REGISTRATIONS } from "../../constants/routeConstants";
import { TYPE_ERROR_MESSAGES } from "../../constants/schemaConstants";
import { schemaPhone, schemaTransformBlankToUndefined } from "../../utils/yupUtils";
import ActionForm from "../ActionForm";
import { CONDITION_TYPES } from "../MenuAdmin/Forms/FormFieldCondition";
import { ConnectedFormBottomComponent } from "./Registrations/FormBottomComponent";

const getValidationSchema = ({ collectGender = true } = {}) =>
  yup.object().shape({
    isOwnProfile: yup.bool().strip(),
    firstName: yup
      .string()
      .trim()
      .required("Please enter a first name."),
    middleNames: yup
      .string()
      .trim()
      .default(""),
    lastName: yup
      .string()
      .trim()
      .required("Please enter a last name."),
    dob: yup
      .string()
      .trim()
      .required("Please enter a date of birth.")
      .test("dateOfBirthIsInPast", "Date of birth must be in the past.", function checkDateOfBirthIsInPast(value) {
        return moment.parseZone(value, "YYYY-MM-DD").isBefore(moment());
      }),
    gender: collectGender
      ? yup
          .string()
          .trim()
          .required("Please select a gender.")
      : yup
          .string()
          .trim()
          .nullable()
          .default(null),
    email: yup
      .string()
      .trim()
      .email(TYPE_ERROR_MESSAGES[FIELD_TYPES.email])
      .default(""),
    phone: schemaPhone(),
    timezoneName: schemaTransformBlankToUndefined(yup.string().default("")),
  });

const PERSON_UPDATE = gql`
  mutation PersonEditForm_PersonUpdate(
    $PersonId: ID!
    $firstName: String!
    $middleNames: String
    $lastName: String!
    $dob: date!
    $gender: String
    $email: String
    $phone: String
    $timezoneName: String
  ) {
    updatePerson(
      PersonId: $PersonId
      dob: $dob
      email: $email
      firstName: $firstName
      gender: $gender
      lastName: $lastName
      middleNames: $middleNames
      phone: $phone
      timezoneName: $timezoneName
    ) {
      returning {
        id
        firstName
        middleNames
        lastName
        dob
        gender
        email
        phone
        timezoneName
      }
    }
  }
`;

function PersonEditForm({
  PersonId,
  initialValues,
  handleSuccess,
  persistId,
  saveContext,
  formControls,
  disabled,
  showFormBottomComponent,
  readOnly,
  useFormValidityForSubmitDisabled,
  step,
}) {
  const history = useHistory();
  const isEditingUserEmail = !history.location.pathname.includes(USER_REGISTRATIONS);

  const currentStep = step || get(formControls, "step", null);
  const collectGender = get(currentStep, "meta.collectGender", true);
  const profileFields = get(currentStep, "meta.profileFields", {}) || {};

  const getFieldLabel = (key, defaultLabel) => {
    const custom = get(profileFields, [key, "heading"], "");
    return custom && custom.trim() ? custom.trim() : defaultLabel;
  };

  const getFieldHelpText = (key, defaultHelpText = undefined) => {
    const custom = get(profileFields, [key, "instruction"], "");
    return custom && custom.trim() ? custom.trim() : defaultHelpText;
  };

  const validationSchema = useMemo(() => getValidationSchema({ collectGender }), [collectGender]);

  const FIELDS = {
    isOwnProfile: {
      type: "hidden",
    },
    personalHeading: {
      type: "heading",
      label: "Personal",
    },
    firstName: {
      type: "text",
      label: getFieldLabel("firstName", "First name"),
      helpText: getFieldHelpText("firstName"),
      required: true,
    },
    middleNames: {
      type: "text",
      label: getFieldLabel("middleNames", "Middle name(s)"),
      helpText: getFieldHelpText("middleNames"),
    },
    lastName: {
      type: "text",
      label: getFieldLabel("lastName", "Last name"),
      helpText: getFieldHelpText("lastName"),
      required: true,
    },
    dob: {
      type: "date",
      label: getFieldLabel("dob", "Date of birth"),
      helpText: getFieldHelpText("dob"),
      required: true,
    },
    ...(collectGender
      ? {
          gender: {
            type: "gender",
            label: getFieldLabel("gender", "Gender"),
            helpText: getFieldHelpText("gender"),
            required: true,
          },
        }
      : {}),
    contactDivider: {
      type: "divider",
    },
    contactHeading: {
      type: "heading",
      label: "Contact",
    },
    email: isEditingUserEmail
      ? {
          type: "email",
          label: getFieldLabel("email", "Email"),
          helpText: getFieldHelpText("email"),
          disabled: true,
          isEditingUserEmail: true,
        }
      : {
          type: "email",
          label: getFieldLabel("email", "Email"),
          helpText: getFieldHelpText("email"),
        },
    phone: {
      type: "phone",
      label: getFieldLabel("phone", "Phone"),
      helpText: getFieldHelpText("phone"),
    },
    locationDivider: {
      type: "divider",
      conditions: [
        {
          type: CONDITION_TYPES.showOnMatch,
          field: "isOwnProfile",
          fieldValue: true,
        },
      ],
    },
    locationHeading: {
      type: "heading",
      label: "Location",
      conditions: [
        {
          type: CONDITION_TYPES.showOnMatch,
          field: "isOwnProfile",
          fieldValue: true,
        },
      ],
    },
    timezoneName: {
      type: "timezone",
      label: "Time zone",
      required: false,
      helpText: "Allows you to see date/time values in the time zone of your choice",
      // See FUS-1028 for an explanation of why the time zone field should only be shown when editing your own profile.
      conditions: [
        {
          type: CONDITION_TYPES.showOnMatch,
          field: "isOwnProfile",
          fieldValue: true,
        },
      ],
    },
  };

  const [personUpdate] = useMutation(PERSON_UPDATE);

  const ControlFormBottomComponent = useCallback(
    props => {
      const { step: ctrlStep, stepIndex, prevStep, nextStep } = formControls;

      return (
        <ConnectedFormBottomComponent
          step={ctrlStep}
          stepIndex={stepIndex}
          prevStep={prevStep}
          nextStep={nextStep}
          {...props}
        />
      );
    },
    [formControls],
  );

  const handleSubmit = useCallback(
    async (values, actions) => {
      try {
        const submissionValues = {
          ...values,
          PersonId,
          gender: collectGender ? values.gender : (values.gender || null),
        };

        await personUpdate({
          variables: submissionValues,
        });

        if (handleSuccess) {
          await handleSuccess();
        }

        actions.setSubmitting(false);
        message.success("Profile updated.");
      } catch (submitError) {
        console.error("PersonEditForm submission error:", submitError);

        actions.setSubmitting(false);
        actions.setStatus({ type: "error" });

        // Check for specific webhook/backend errors
        const errorMessage = submitError?.message || "";
        if (errorMessage.includes("not a valid json response from webhook")) {
          message.error("Server error: The backend service is not responding correctly. Please try again or contact support.");
          console.error("Backend webhook error - check Fango/Hasura webhook configuration");
        } else {
          message.error("Failed to update profile.");
        }

        Sentry.captureException(submitError, {
          contexts: {
            personUpdate: {
              PersonId,
              errorType: errorMessage.includes("webhook") ? "webhook_error" : "general_error",
            },
          },
        });
      }
    },
    [PersonId, personUpdate, handleSuccess, collectGender],
  );

  return (
    <ActionForm
      disabled={disabled}
      fields={FIELDS}
      validationSchema={validationSchema}
      FormBottomComponent={formControls ? ControlFormBottomComponent : null}
      initialValues={initialValues}
      handleSubmit={handleSubmit}
      saveContext={saveContext}
      persistId={persistId}
      showFormBottomComponent={showFormBottomComponent}
      readOnly={readOnly}
      submitText="Save"
      useFormValidityForSubmitDisabled={useFormValidityForSubmitDisabled}
    />
  );
}

PersonEditForm.propTypes = {
  PersonId: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  formControls: PropTypes.object,
  handleSuccess: PropTypes.func,
  persistId: PropTypes.string,
  saveContext: PropTypes.bool,
  disabled: PropTypes.bool,
  showFormBottomComponent: PropTypes.bool,
  readOnly: PropTypes.bool,
  useFormValidityForSubmitDisabled: PropTypes.bool,
  step: PropTypes.object,
};

PersonEditForm.defaultProps = {
  handleSuccess: null,
  saveContext: false,
  persistId: "",
  formControls: null,
  disabled: false,
  showFormBottomComponent: true,
  readOnly: false,
  useFormValidityForSubmitDisabled: false,
  step: null,
};

export default PersonEditForm;
